// @ts-strict-ignore
import LocalizedLink from '@components/Localization/LocalizedLink'
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import { pickSeoProps } from '@components/Page/Page'
import ContactForm from '@components/_contact/ContactForm'
import { Box, Container, Grid, OldHeading, Image, Link, OldText } from '@gassan-ui'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { getUrlSlugInLocale } from '@lib/get-url-slug-in-locale'
import { SingleItemResponse, getKontentImage, getPageByCodename } from '@lib/kontent'
import { ContactPage as ContactPageModel } from '@lib/kontent/models/ContactPage'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

type Props = SingleItemResponse<ContactPageModel>

const ContactPage: NextPage<Props> = ({ item }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('other')
  const router = useRouter()
  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(router.locales, null, '/contact')}
    >
      <FloatingServiceMenu />
      <Header initialVariant="absolute" />
      <PageTransition>
        <Container
          pt={{ _: '2rem', small: '3rem', large: '5rem', xlarge: '7rem' }}
          pb={{ _: '0', large: '15rem', xlarge: '7.5rem' }}
        >
          <Grid
            gridTemplateColumns={{
              _: '1fr',
              large: '1fr 1fr',
              xlarge: 'minmax(auto, 25rem) 1fr 1fr',
            }}
            gridGap={{
              _: '1rem',
              small: '3rem',
              xlarge: '2rem',
            }}
            justifyItems="stretch"
          >
            <Box>
              <OldHeading variant="h1" as="h1" mb={{ _: '1.5rem', large: '2rem' }}>
                Contact
              </OldHeading>
              <Box mb={{ _: '2rem', small: '3rem', xlarge: '4.5rem' }} gridColumn="1">
                <Box
                  mb={{ _: '2rem', small: '3rem', xlarge: '4.5rem' }}
                  gridColumn={{ _: 'auto', small: 2, large: 'auto' }}
                  gridRow={{ _: 'auto', small: 2, large: 'auto' }}
                >
                  <OldText mb={{ _: '1rem', small: '1.5rem' }}>
                    {t('needStoreContactDetails')}
                  </OldText>
                  <LocalizedLink href={`/${getUrlSlugInLocale('stores', language)}`}>
                    <Link variant="brown">{t('showAllStores')}</Link>
                  </LocalizedLink>
                </Box>
              </Box>
              <Box mb={{ _: '2rem', small: '3rem', xlarge: '4.5rem' }} gridColumn="1">
                <OldText variant="regularBold" mb="0">
                  {item.elements.info_and_opening_hours_title.value}
                </OldText>
                <OldText
                  as="div"
                  mb="0"
                  dangerouslySetInnerHTML={{
                    __html: item.elements.info_and_opening_hours_content.value,
                  }}
                />
              </Box>
              <Box mb={{ _: '2rem', small: '3rem', xlarge: '4.5rem' }} gridColumn="1">
                <OldText variant="regularBold" mb="0">
                  {item.elements.service_title.value}
                </OldText>
                <OldText
                  as="div"
                  mb="0"
                  dangerouslySetInnerHTML={{
                    __html: item.elements.service_content.value,
                  }}
                />
              </Box>
            </Box>
            <Box
              width={{ _: 'calc(100% + 2rem)', small: '100%' }}
              p={{ _: '1rem', small: '4.5rem', large: 0 }}
              pt={{ _: '2rem', small: '4.5rem', large: 0 }}
              ml={{ _: '-1rem', small: 'initial' }}
              maxWidth={{ _: 'initial', large: '36rem' }}
              bg={{ _: 'pampasLight', large: 'initial' }}
            >
              <OldHeading variant="h3" as="h2" mb="1">
                {item.elements.contact_form_title.value}
              </OldHeading>
              <OldText variant="regular" mb="4">
                {item.elements.intro_content.value}
              </OldText>
              <ContactForm />
            </Box>
            <Box
              width="100%"
              maxWidth="36rem"
              justifySelf="end"
              display={{ _: 'none', xlarge: 'block' }}
            >
              <Image
                placeholderSrc={getKontentImage(item.elements.image.value[0].url, { width: 50 })}
                sources={[getKontentImage(item.elements.image.value[0].url, { width: 560 })]}
                className="aspect-[5/8]"
              />
            </Box>
          </Grid>
        </Container>
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default ContactPage

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const content = await getPageByCodename<ContactPageModel>({
    codename: 'gassan_contact_page',
    lang: locale,
    preview,
  })
  return {
    props: {
      ...content,
      ...(await getServerSideTranslations(locale)),
    },
  }
}
